import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import { useToast } from "../../../../hooks/useToast";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

interface Appointment {
  id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: string;
  notes: string;
  caregiver_id: string | null;
  caregiver_name?: string;
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/client", icon: "ri-dashboard-line" },
  { label: "Appointments", path: "/dashboard/client/appointments", icon: "ri-calendar-event-line" },
  { label: "Messages", path: "/dashboard/client/messages", icon: "ri-chat-3-line" },
  { label: "Settings", path: "/dashboard/client/settings", icon: "ri-settings-3-line" },
];

export default function ClientAppointments() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState("");

  useEffect(() => {
    if (!user) return;
    fetchAppointments();

    // Real-time subscription for appointments
    const channel = supabase
      .channel('appointments-client')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'appointments',
        filter: `client_id=eq.${user.id}`,
      }, (payload) => {
        fetchAppointments();
        if (payload.eventType === 'INSERT') showToast('New appointment scheduled', 'success');
        if (payload.eventType === 'UPDATE') showToast('Appointment updated', 'info');
        if (payload.eventType === 'DELETE') showToast('Appointment cancelled', 'error');
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchAppointments = async () => {
    const { data } = await supabase
      .from("appointments")
      .select("*, caregiver:profiles!appointments_caregiver_id_fkey(full_name)")
      .eq("client_id", user!.id)
      .order("appointment_date", { ascending: false });
    const mapped = (data || []).map((a: any) => ({
      id: a.id,
      appointment_date: a.appointment_date,
      start_time: a.start_time,
      end_time: a.end_time,
      status: a.status,
      notes: a.notes,
      caregiver_id: a.caregiver_id,
      caregiver_name: a.caregiver?.full_name || "Unassigned",
    }));
    setAppointments(mapped);
    setLoading(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      scheduled: "bg-amber-50 text-amber-700",
      completed: "bg-brand-green-50 text-brand-green-700",
      cancelled: "bg-red-50 text-red-700",
      no_show: "bg-gray-100 text-gray-600",
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${styles[status] || "bg-gray-100 text-gray-600"}`}>
        {status}
      </span>
    );
  };

  const filtered = filter === "all" ? appointments : appointments.filter((a) => a.status === filter);

  const handleNewAppointment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormMsg("");
    const fd = new FormData(e.currentTarget);
    const date = fd.get("date") as string;
    const startTime = fd.get("start_time") as string;
    const endTime = fd.get("end_time") as string;
    const notes = fd.get("notes") as string;

    const { error } = await supabase.from("appointments").insert({
      client_id: user!.id,
      appointment_date: date,
      start_time: startTime,
      end_time: endTime,
      notes,
      status: "scheduled",
    });

    if (error) {
      setFormMsg("Failed to schedule appointment. Please try again.");
    } else {
      setFormMsg("Appointment scheduled successfully!");
      setTimeout(() => {
        setShowNewModal(false);
        setFormMsg("");
        fetchAppointments();
      }, 1200);
    }
    setSubmitting(false);
  };

  return (
    <DashboardLayout navItems={navItems} title="Client Portal" subtitle="Appointments">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading appointments...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Header + Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setFilter("all")}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filter === "all" ? "bg-brand-green-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-brand-green-300"}`}
              >
                All
              </button>
              <button
                onClick={() => setFilter("scheduled")}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filter === "scheduled" ? "bg-amber-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-amber-300"}`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setFilter("completed")}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${filter === "completed" ? "bg-brand-green-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-brand-green-300"}`}
              >
                Completed
              </button>
            </div>
            <button
              onClick={() => setShowNewModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </div>
              Schedule Visit
            </button>
          </div>

          {/* Appointments List */}
          {filtered.length > 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="divide-y divide-gray-100">
                {filtered.map((appt) => (
                  <div key={appt.id} className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center shrink-0">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-calendar-check-line text-brand-green-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{formatDate(appt.appointment_date)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {appt.start_time} – {appt.end_time} · Caregiver: {appt.caregiver_name}
                      </p>
                      {appt.notes && (
                        <p className="text-xs text-gray-400 mt-1 truncate">{appt.notes}</p>
                      )}
                    </div>
                    <div className="shrink-0">{statusBadge(appt.status)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 border border-gray-100 text-center">
              <div className="w-14 h-14 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-calendar-line text-xl text-gray-400" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">No appointments found.</p>
              <button
                onClick={() => setShowNewModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap"
              >
                Schedule Your First Visit
              </button>
            </div>
          )}
        </div>
      )}

      {/* New Appointment Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowNewModal(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule New Visit</h3>
            {formMsg && (
              <div className={`rounded-lg p-3 border mb-4 ${formMsg.includes("success") ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800" : "bg-red-50 border-red-100 text-red-700"}`}>
                <p className="text-sm text-center">{formMsg}</p>
              </div>
            )}
            <form onSubmit={handleNewAppointment} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Date</label>
                <input
                  type="date"
                  name="date"
                  required
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Start Time</label>
                  <input
                    type="time"
                    name="start_time"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">End Time</label>
                  <input
                    type="time"
                    name="end_time"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Notes (optional)</label>
                <textarea
                  name="notes"
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none"
                  placeholder="Any special instructions or requests..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-soft-gray rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
                >
                  {submitting ? "Scheduling..." : "Schedule"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}